
/**
 * CollectionMethod Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class CollectionMethodService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'CollectionMethod listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch collection-method list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch collection-method');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CollectionMethod oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create collection-method');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CollectionMethod güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update collection-method');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CollectionMethod silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete collection-method');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Get dropdown parameters
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('CollectionMethodService');
        try {
            const response = await this.api.getDropdown();
            return {
                success: true,
                data: response.data || [],
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown params');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
